import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { remindersService, WhatsAppReminder } from '@/services/reminders';
import {
  MessageCircle,
  Phone,
  User,
  Stethoscope,
  Syringe,
  Bug,
  Calendar,
  CheckCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  Smartphone,
  Monitor,
  Scissors,
  HeartPulse,
  MessageSquare,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import ReminderTemplatesModal from '@/components/ReminderTemplatesModal';
import { useAuthStore } from '@/context/authStore';
import clsx from 'clsx';
import toast from 'react-hot-toast';

export default function Reminders() {
  const [sentReminders, setSentReminders] = useState<Set<string>>(new Set());
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  // El superadmin impersonando a una veterinaria entra con rol superadmin en el store,
  // asi que tambien tiene que ver el boton (el token con el que guarda es de admin).
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin' || role === 'superadmin';
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Cerrar dropdown al hacer click afuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Query para obtener recordatorios
  const { data: remindersData, isLoading, refetch } = useQuery({
    queryKey: ['reminders', 'whatsapp'],
    queryFn: () => remindersService.getWhatsAppReminders(),
  });

  // Agrupar recordatorios por tipo
  const groupedReminders = useMemo(() => {
    if (!remindersData?.reminders) return { visits: [], vaccinations: [], dewormings: [], grooming: [], medical: [] };

    const visits = remindersData.reminders
      .filter(r => r.type === 'visit')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const vaccinations = remindersData.reminders
      .filter(r => r.type === 'vaccination')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const dewormings = remindersData.reminders
      .filter(r => r.type === 'deworming')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const grooming = remindersData.reminders
      .filter(r => r.type === 'grooming')
      .sort((a, b) => {
        // Para grooming, ordenar por fecha + hora
        const dateTimeA = new Date(`${a.date}T${a.scheduled_time || '00:00:00'}`).getTime();
        const dateTimeB = new Date(`${b.date}T${b.scheduled_time || '00:00:00'}`).getTime();
        return dateTimeA - dateTimeB;
      });

    const medical = remindersData.reminders
      .filter(r => r.type === 'medical')
      .sort((a, b) => {
        const dateTimeA = new Date(`${a.date}T${a.scheduled_time || '00:00:00'}`).getTime();
        const dateTimeB = new Date(`${b.date}T${b.scheduled_time || '00:00:00'}`).getTime();
        return dateTimeA - dateTimeB;
      });

    return { visits, vaccinations, dewormings, grooming, medical };
  }, [remindersData]);

  // Mutation para marcar como enviado
  const markSentMutation = useMutation({
    mutationFn: ({ recordType, recordId }: { recordType: 'visit' | 'vaccination' | 'deworming' | 'grooming' | 'medical'; recordId: number }) =>
      remindersService.markReminderSent(recordType, recordId),
    onSuccess: (_, variables) => {
      const key = `${variables.recordType}-${variables.recordId}`;
      setSentReminders(prev => new Set(prev).add(key));
      toast.success('Recordatorio marcado como enviado');
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
    onError: () => {
      toast.error('Error al marcar como enviado');
    },
  });

  const handleMarkSent = (reminder: WhatsAppReminder) => {
    markSentMutation.mutate({
      recordType: reminder.type,
      recordId: reminder.record_id,
    });
  };

  const isReminderSent = (reminder: WhatsAppReminder): boolean => {
    const key = `${reminder.type}-${reminder.record_id}`;
    return sentReminders.has(key);
  };

  // Abrir WhatsApp con el mensaje - soporta App y Web
  const openWhatsApp = (reminder: WhatsAppReminder, target: 'app' | 'web'): void => {
    // Sin telefono no hay WhatsApp posible (el recordatorio igual se muestra).
    if (!reminder.owner_phone) {
      toast.error('Este cliente no tiene teléfono cargado. Agregalo en su ficha para poder avisarle.');
      return;
    }
    // Normalizar telefono argentino
    let phone = reminder.owner_phone.replace(/\D/g, '');
    if (phone.startsWith('54')) phone = phone.slice(2);
    if (phone.startsWith('9')) phone = phone.slice(1);
    if (phone.startsWith('0')) phone = phone.slice(1);
    if (phone.length >= 12 && phone.slice(2, 4) === '15') {
      phone = phone.slice(0, 2) + phone.slice(4);
    } else if (phone.length >= 12 && phone.slice(3, 5) === '15') {
      phone = phone.slice(0, 3) + phone.slice(5);
    }
    phone = '549' + phone;

    // El mensaje lo arma el backend con la plantilla que cargo la veterinaria
    // (reminder_template_*). Rearmarlo aca hacia que editar la plantilla no
    // cambiara nada de lo que se enviaba.
    const message = reminder.whatsapp_message;
    if (!message) {
      toast.error('No se pudo armar el mensaje del recordatorio');
      return;
    }

    const encodedText = encodeURIComponent(message);

    if (target === 'app') {
      // Protocolo whatsapp:// abre la app directamente (emojis funcionan bien)
      const url = `whatsapp://send?phone=${phone}&text=${encodedText}`;
      window.location.href = url;
    } else {
      // web.whatsapp.com funciona si el usuario tiene sesión iniciada (emojis se ven bien)
      const url = `https://web.whatsapp.com/send?phone=${phone}&text=${encodedText}`;
      window.open(url, '_blank');
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Manana';
    if (diffDays <= 7) return `En ${diffDays} dias`;
    return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Componente para renderizar una tarjeta de recordatorio
  const ReminderCard = ({ reminder }: { reminder: WhatsAppReminder }) => {
    const isSent = isReminderSent(reminder);

    return (
      <div
        className={clsx(
          'p-3 rounded-lg border transition-all overflow-visible',
          isSent && 'opacity-50'
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={clsx('font-semibold', isSent ? 'text-gray-500' : 'text-gray-900')}>
                {reminder.patient_name}
              </span>
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded-full font-medium',
                isSent ? 'bg-gray-200 text-gray-600'
                  : reminder.is_expired ? 'bg-red-100 text-red-700'
                  : 'bg-amber-100 text-amber-800'
              )}>
                {formatDate(reminder.date)}
              </span>
              {reminder.is_expired && !isSent && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-500 text-white">
                  Vencido
                </span>
              )}
              {isSent && (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span className="truncate">{reminder.owner_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {reminder.owner_phone ? (
                  <span>{reminder.owner_phone}</span>
                ) : (
                  <span className="text-orange-600 font-medium">Sin teléfono cargado</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span className="text-xs">
                  {reminder.type === 'visit'
                    ? reminder.reason
                    : reminder.type === 'vaccination'
                    ? reminder.vaccine_name
                    : reminder.type === 'medical'
                    ? `${reminder.type_name || (reminder.category === 'surgery' ? 'Cirugía' : 'Ecografía')}${reminder.scheduled_time ? ` - ${reminder.scheduled_time}` : ''}`
                    : `${reminder.service_type}${reminder.scheduled_time ? ` - ${reminder.scheduled_time}` : ''}`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {!isSent && (
          <div className="mt-2 flex gap-2">
            <div className="relative flex-1" ref={openDropdown === `${reminder.type}-${reminder.record_id}` ? dropdownRef : undefined}>
              <button
                disabled={!reminder.owner_phone}
                title={reminder.owner_phone ? 'Enviar por WhatsApp' : 'El cliente no tiene teléfono cargado'}
                onClick={() => setOpenDropdown(
                  openDropdown === `${reminder.type}-${reminder.record_id}`
                    ? null
                    : `${reminder.type}-${reminder.record_id}`
                )}
                className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600"
              >
                <MessageCircle className="h-3 w-3" />
                WhatsApp
                <ChevronDown className="h-3 w-3" />
              </button>
              {reminder.owner_phone && openDropdown === `${reminder.type}-${reminder.record_id}` && (
                <div className="absolute left-0 right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border z-50">
                  <button
                    onClick={() => {
                      openWhatsApp(reminder, 'app');
                      setOpenDropdown(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-t-lg"
                  >
                    <Smartphone className="h-3 w-3" />
                    Abrir en App
                  </button>
                  <button
                    onClick={() => {
                      openWhatsApp(reminder, 'web');
                      setOpenDropdown(null);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-b-lg border-t"
                  >
                    <Monitor className="h-3 w-3" />
                    Abrir en Web
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => handleMarkSent(reminder)}
              disabled={markSentMutation.isPending}
              className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-xs"
            >
              <CheckCircle className="h-3 w-3" />
              Enviado
            </button>
          </div>
        )}
      </div>
    );
  };

  // Componente para una fila/swimlane horizontal
  const Swimlane = ({
    title,
    icon: Icon,
    reminders,
    bgColor,
    borderColor,
    iconColor,
    emptyText
  }: {
    title: string;
    icon: React.ElementType;
    reminders: WhatsAppReminder[];
    bgColor: string;
    borderColor: string;
    iconColor: string;
    emptyText: string;
  }) => (
    <div className={clsx('rounded-xl border-2 overflow-hidden', borderColor)}>
      <div className={clsx('px-4 py-3 flex items-center justify-between', bgColor)}>
        <div className="flex items-center gap-2">
          <Icon className={clsx('h-5 w-5', iconColor)} />
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        <span className="text-sm bg-white/50 px-2 py-0.5 rounded-full text-gray-700">
          {reminders.length}
        </span>
      </div>
      <div className="p-3 bg-white overflow-visible">
        {reminders.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-4">{emptyText}</p>
        ) : (() => {
          const currentYear = new Date().getFullYear();
          const byYear: Record<number, WhatsAppReminder[]> = {};
          reminders.forEach((r) => {
            const year = new Date(r.date + 'T00:00:00').getFullYear();
            if (!byYear[year]) byYear[year] = [];
            byYear[year].push(r);
          });
          const years = Object.keys(byYear).map(Number).sort();
          const needsGrouping = years.length > 1;

          return (
            <div className="space-y-4">
              {years.map((year) => (
                <div key={year}>
                  {needsGrouping && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className={clsx(
                        'text-xs font-semibold px-2 py-0.5 rounded-full',
                        year === currentYear
                          ? 'bg-gray-200 text-gray-700'
                          : 'bg-amber-100 text-amber-800'
                      )}>
                        {year === currentYear ? `${year} (este ano)` : year}
                      </span>
                      <div className="flex-1 border-t border-gray-200"></div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 overflow-visible">
                    {byYear[year].map((reminder) => (
                      <ReminderCard key={`${reminder.type}-${reminder.record_id}`} reminder={reminder} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recordatorios WhatsApp</h1>
          <p className="text-gray-500 mt-1">
            Envia recordatorios a tus clientes por WhatsApp
          </p>
        </div>
        <div className="flex gap-2">
        {isAdmin && (
          <Button variant="secondary" onClick={() => setShowTemplatesModal(true)}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Editar mensaje
          </Button>
        )}
        <Button
          onClick={() => refetch()}
          disabled={isLoading}
          variant="secondary"
          className="border-green-300 text-green-700 hover:bg-green-50"
        >
          <RefreshCw className={clsx('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
          Actualizar
        </Button>
        </div>
      </div>

      {/* Editar los mensajes de WhatsApp */}
      <Modal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        title="Mensajes de los recordatorios"
        size="lg"
      >
        <ReminderTemplatesModal onClose={() => setShowTemplatesModal(false)} />
      </Modal>

      {/* Info Card */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex gap-3">
          <MessageCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            <p className="font-medium mb-1">Como funciona</p>
            <ol className="list-decimal list-inside space-y-1 text-green-700">
              <li>Hace click en "Enviar" y elegi App o Web</li>
              <li>Se abre el chat con el mensaje ya escrito</li>
              <li>Presiona Enviar en WhatsApp y marca como enviado aca</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Total count */}
      {remindersData && remindersData.total_reminders > 0 && (
        <div className="text-sm text-gray-600">
          Total: <span className="font-medium">{remindersData.total_reminders}</span> recordatorio(s) pendiente(s)
        </div>
      )}

      {/* Swimlanes */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : remindersData?.total_reminders === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="text-center py-12 text-gray-500">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No hay recordatorios pendientes</p>
            <p className="text-sm mt-1">
              Los recordatorios aparecen cuando hay pacientes con controles, vacunas
              o turnos de peluqueria programados y el dueno tiene telefono registrado
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <Swimlane
            title="Consultas"
            icon={Stethoscope}
            reminders={groupedReminders.visits}
            bgColor="bg-blue-100"
            borderColor="border-blue-300"
            iconColor="text-blue-600"
            emptyText="Sin consultas pendientes"
          />
          <Swimlane
            title="Vacunacion"
            icon={Syringe}
            reminders={groupedReminders.vaccinations}
            bgColor="bg-green-100"
            borderColor="border-green-300"
            iconColor="text-green-600"
            emptyText="Sin vacunas pendientes"
          />
          <Swimlane
            title="Desparasitacion"
            icon={Bug}
            reminders={groupedReminders.dewormings}
            bgColor="bg-amber-100"
            borderColor="border-amber-300"
            iconColor="text-amber-600"
            emptyText="Sin desparasitaciones pendientes"
          />
          <Swimlane
            title="Peluqueria"
            icon={Scissors}
            reminders={groupedReminders.grooming}
            bgColor="bg-pink-100"
            borderColor="border-pink-300"
            iconColor="text-pink-600"
            emptyText="Sin turnos pendientes"
          />
          <Swimlane
            title="Turnos Medicos"
            icon={HeartPulse}
            reminders={groupedReminders.medical}
            bgColor="bg-red-100"
            borderColor="border-red-300"
            iconColor="text-red-600"
            emptyText="Sin turnos medicos pendientes"
          />
        </div>
      )}
    </div>
  );
}
